import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { decamelize } from 'humps';

import { useAgenticGetChatQuery } from '../hooks/useAgenticGetChatQuery';
import { useAgenticCreateChatMessageMutation } from '../hooks/useAgenticCreateChatMessageMutation';
import { useAgenticCreateChatMutation } from '../hooks/useAgenticCreateChatMutation';
import { useAgenticGetChatsQuery } from '../hooks/useAgenticGetChatsQuery';

import { useNotifications } from '../../../providers';
import { useUsers, useSources, useOrganizations } from '../../organization/providers';
import { useAuth } from '../../authentication';
import { useTermTranslations } from './TermTranslationsProvider';
import { useSpaces } from './SpacesProvider';
import { useDatabaseDescription } from './DatabaseDescriptionProvider';
import { mergeSourcesIntoColumns } from '../../../utils';

const AgenticAssetBuilderContext = createContext();

const AgenticAssetBuilderProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  // #region Internal state
  const [isChartMode, setIsChartMode] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [matchingEntities, setMatchingEntities] = useState([]);
  // #endregion

  // #region Data providers
  const { currentUser, users } = useUsers();
  const { sources } = useSources();
  const { termTranslations, metrics } = useTermTranslations();
  const { currentOrganization } = useOrganizations();
  const { databaseDescription } = useDatabaseDescription();
  const { spaces, createSpace } = useSpaces();

  // #region Agentic chat hooks
  const { data: currentChat, isLoading: isFetchingChat } = useAgenticGetChatQuery(
    accessToken,
    conversationId,
    currentOrganization?.id
  );

  const { isLoading: isFetchingChats } = useAgenticGetChatsQuery(
    accessToken,
    currentOrganization?.id
  );

  const { mutateAsync: createChatMessage } = useAgenticCreateChatMessageMutation(accessToken);
  const { mutateAsync: createChat } = useAgenticCreateChatMutation(accessToken);
  // #endregion

  // #region Derived values
  const dbSchemasWithSources = useMemo(() => {
    if (!sources) return null;
    if (!databaseDescription) return null;

    return mergeSourcesIntoColumns(sources, databaseDescription);
  }, [sources, databaseDescription]);

  const columnMentionItems = useMemo(() => {
    if (!dbSchemasWithSources || !Array.isArray(dbSchemasWithSources)) return null;

    try {
      return dbSchemasWithSources
        .flatMap((schema) => {
          if (!schema || !schema.views) return [];

          return Object.values(schema.views).flatMap((view) => {
            if (!view || !view.columns || !Array.isArray(view.columns)) return [];

            const viewName = view.name?.split('.')?.[1] ? view.name?.split('.')?.[1] : view.name;
            return view.columns.map((column) => ({
              value: decamelize(`${schema.name}.${viewName}.${column.name}`),
              schema: decamelize(schema.name),
              view: decamelize(viewName),
              column: decamelize(column.name),
              columnDescription: column.description,
              viewDescription: view.description,
              schemaDescription: schema.description,
              enumeratedValues: column.enumeratedValues,
              source: column.source
            }));
          });
        })
        .sort((a, b) => a.value.localeCompare(b.value));
    } catch (error) {
      return null;
    }
  }, [dbSchemasWithSources]);

  // Convert API response to conversation format
  const currentConversation = useMemo(() => {
    if (!currentChat) return null;

    const conversation = {
      id: currentChat.id,
      title: currentChat.title || 'Agentic Chat',
      organizationId: currentChat.organization_id || currentOrganization?.id,
      userId: currentChat.user_id,
      messages: currentChat.messages || [],
      createdAt: currentChat.created_at,
      updatedAt: currentChat.updated_at
    };

    return conversation;
  }, [currentChat, currentOrganization?.id]);

  // Convert messages to questions format for compatibility with ChatItem component
  const questions = useMemo(() => {
    if (!currentConversation?.messages || currentConversation.messages.length === 0) {
      return [];
    }

    const convertedQuestions = currentConversation.messages.map((message, index) => {
      // Get the first reply from the replies array
      const firstReply = message.replies?.[0];

      const question = {
        id: message.id,
        body: message.body,
        conversationId: message.chat_id,
        userId: message.user_id,
        organizationId: message.organization_id,
        createdAt: message.created_at,
        updatedAt: message.updated_at,
        // Convert first reply to answer format for compatibility with ChatItem
        answer: firstReply
          ? {
              id: firstReply.id,
              body: firstReply.body,
              dataDescription: firstReply.body,
              chartData: null,
              queryResult: null,
              status: 'completed',
              createdAt: firstReply.created_at,
              updatedAt: firstReply.updated_at,
              createdBy: firstReply.created_by
            }
          : {
              id: `answer_${message.id}`,
              body: 'Processing...',
              dataDescription: 'Processing...',
              chartData: null,
              queryResult: null,
              status: 'processing'
            },
        status: firstReply ? null : 'processing',
        isChart: false,
        isClarify: false,
        isCategoryQuestion: false,
        previousQuestionId: index > 0 ? currentConversation.messages[index - 1].id : null,
        followUps: [],
        childQuestions: message.replies || [],
        spaceIds: [],
        assetTitle: null,
        displayTitle: message.body.substring(0, 50) + (message.body.length > 50 ? '...' : ''),
        // Add user information
        user: users.find((u) => u.id === message.user_id) || {
          id: message.user_id,
          name: 'Unknown User'
        }
      };

      return question;
    });

    return convertedQuestions;
  }, [currentConversation?.messages, users]);

  // #endregion

  // #region Callbacks & Functions
  const handleCreateQuestion = useCallback(
    async (body, chatId = null) => {
      try {
        let targetChatId = chatId || conversationId;

        if (!currentOrganization?.id) {
          addNotification({
            message: 'Error: No organization ID available',
            icon: 'alert'
          });
          return;
        }

        // If no chat ID, create a new agentic chat first
        if (!targetChatId) {
          const newChat = await createChat({
            organizationId: currentOrganization.id
          });

          targetChatId = newChat.data.id;

          // Navigate to the new chat URL
          const newChatUrl = `/agentic-chats/${targetChatId}?organization_id=${currentOrganization.id}`;
          navigate(newChatUrl);
        }

        await createChatMessage({
          chatId: targetChatId,
          organizationId: currentOrganization.id,
          body
        });

        // Invalidate cache to refresh the chat data
        queryClient.invalidateQueries(['agentic', 'chats', targetChatId]);
        queryClient.invalidateQueries(['agentic', 'chats', currentOrganization.id]);

        addNotification({
          message: 'Message sent successfully',
          icon: 'check'
        });
      } catch (error) {
        addNotification({
          message: `Error sending message: ${error.response?.data?.message || error.message}`,
          icon: 'alert'
        });
      }
    },
    [
      createChatMessage,
      createChat,
      conversationId,
      currentOrganization?.id,
      currentUser?.id,
      addNotification,
      navigate,
      queryClient
    ]
  );

  const handleSelectEntity = useCallback(async (selectedText) => {
    // Simplified entity selection - just update state
    if (selectedText && selectedText.trim()) {
      setMatchingEntities([]);
    }
  }, []);

  const handleShare = useCallback(() => {
    // Simplified share functionality
    addNotification({
      message: 'Share functionality not yet implemented for agentic chats',
      icon: 'info'
    });
  }, [addNotification]);

  const handleUpdateConversation = useCallback(() => {
    // Simplified conversation update
    addNotification({
      message: 'Conversation update functionality not yet implemented for agentic chats',
      icon: 'info'
    });
  }, [addNotification]);

  // Mock empty functions for compatibility
  const handleUserFeedback = useCallback(() => {}, []);
  const handleUpdateAssetTitle = useCallback(() => {}, []);
  const handleUpdateAnswer = useCallback(() => {}, []);
  const handleAddQuestionToSpace = useCallback(() => {}, []);
  const handleRemoveQuestionFromSpace = useCallback(() => {}, []);
  const handleSelectQuestion = useCallback(() => {}, []);
  const handleDeleteQuestion = useCallback(() => {}, []);
  const handleRetryQuestion = useCallback(() => {}, []);
  const handleCreateAnswer = useCallback(() => {}, []);
  const handleBranchChange = useCallback(() => {}, []);
  const findEntityMatches = useCallback(() => {}, []);

  // #endregion

  const value = useMemo(
    () => ({
      isShared: false,
      isChartMode,
      isFetchingQuestions: isFetchingChat || isFetchingChats,
      setIsChartMode,
      selectionPath: new Map(),
      selectedQuestion,
      setSelectedQuestion,
      sources,
      users,
      participants: [],
      termTranslations,
      databaseDescription,
      currentConversation,
      currentOrganization,
      currentUser,
      columnMentionItems,
      questions,
      matchingEntities,
      handleSelectEntity,
      handleShare,
      handleUserFeedback,
      handleUpdateAssetTitle,
      handleUpdateAnswer,
      handleAddQuestionToSpace,
      handleRemoveQuestionFromSpace,
      handleCreateQuestion,
      handleSelectQuestion,
      handleDeleteQuestion,
      handleRetryQuestion,
      handleUpdateConversation,
      spaces,
      assets: [],
      createSpace,
      metrics,
      handleCreateAnswer,
      handleBranchChange,
      findEntityMatches,
      accessToken
    }),
    [
      isChartMode,
      isFetchingChat,
      isFetchingChats,
      selectedQuestion,
      sources,
      users,
      termTranslations,
      databaseDescription,
      currentConversation,
      currentOrganization,
      currentUser,
      columnMentionItems,
      questions,
      matchingEntities,
      handleSelectEntity,
      handleShare,
      handleUserFeedback,
      handleUpdateAssetTitle,
      handleUpdateAnswer,
      handleAddQuestionToSpace,
      handleRemoveQuestionFromSpace,
      handleCreateQuestion,
      handleSelectQuestion,
      handleDeleteQuestion,
      handleRetryQuestion,
      handleUpdateConversation,
      spaces,
      createSpace,
      metrics,
      handleCreateAnswer,
      handleBranchChange,
      findEntityMatches,
      accessToken
    ]
  );

  return (
    <AgenticAssetBuilderContext.Provider value={value}>
      {children}
    </AgenticAssetBuilderContext.Provider>
  );
};

AgenticAssetBuilderProvider.propTypes = {
  children: PropTypes.node
};

const useAgenticAssetBuilder = () => useContext(AgenticAssetBuilderContext);

export { AgenticAssetBuilderProvider, AgenticAssetBuilderContext, useAgenticAssetBuilder };
